"use client"

import { ChevronLeft, ChevronRight, Menu, Search, X } from "lucide-react"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import * as React from "react"

import AppTitle from "@/components/layout/AppTitle"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/input"
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import type { GetMenuQuery } from "@/lib/gql/solutions-graphql"
import {
  getMenuItemUrl,
  isExternalUrl,
  isMenuItemActive,
} from "@/lib/menu/menu-active-utils"

// --------------------
// Types
// --------------------
// Use codegen types from GraphQL query
export type MenuData = NonNullable<GetMenuQuery["menu"]>
export type MenuItem = MenuData["items"][number]

interface DrupalMenuProps {
  className?: string
  /** Menu data fetched from Drupal via getMenu() - uses codegen types */
  menuData: GetMenuQuery["menu"]
  /** Optional fallback menu if Drupal menu is not available */
  fallbackMenu?: GetMenuQuery["menu"]
}

export function DrupalMenu({
  className,
  menuData,
  fallbackMenu,
}: DrupalMenuProps) {
  const pathname = usePathname()
  const router = useRouter()
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const [activeItem, setActiveItem] = React.useState<HTMLElement | null>(null)
  const [searchOpen, setSearchOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")
  const menuListRef = React.useRef<HTMLUListElement>(null)
  const [openGroupsSheets, setOpenGroupsSheets] = React.useState<
    Record<string, boolean>
  >({})
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  // Use fallback menu if main menu is not available
  const menu = menuData || fallbackMenu

  // Gérer le survol des éléments du menu
  const handleMouseEnter = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.currentTarget
      if (!menuListRef.current) return

      const rect = target.getBoundingClientRect()
      const listRect = menuListRef.current.getBoundingClientRect()

      menuListRef.current.style.setProperty(
        "--underline-width",
        `${rect.width}px`
      )
      menuListRef.current.style.setProperty(
        "--underline-x",
        `${rect.left - listRect.left}px`
      )
    },
    []
  )

  // Réinitialiser la position de la bordure quand la souris quitte le menu
  const handleMouseLeave = React.useCallback(() => {
    if (!menuListRef.current) return

    // Si on a un élément actif, on garde sa bordure
    if (activeItem) {
      const rect = activeItem.getBoundingClientRect()
      const listRect = menuListRef.current.getBoundingClientRect()
      menuListRef.current.style.setProperty(
        "--underline-width",
        `${rect.width}px`
      )
      menuListRef.current.style.setProperty(
        "--underline-x",
        `${rect.left - listRect.left}px`
      )
    } else {
      // Sinon on réinitialise la bordure
      menuListRef.current.style.setProperty("--underline-width", "0px")
      menuListRef.current.style.setProperty("--underline-x", "0px")
    }
  }, [activeItem])

  // Mettre à jour l'élément actif quand le pathname change
  React.useEffect(() => {
    if (!menuListRef.current) return

    const activeLink = menuListRef.current.querySelector(
      '[aria-current="page"]'
    ) as HTMLElement
    if (activeLink) {
      setActiveItem(activeLink)
      const rect = activeLink.getBoundingClientRect()
      const listRect = menuListRef.current.getBoundingClientRect()

      menuListRef.current.style.setProperty(
        "--underline-width",
        `${rect.width}px`
      )
      menuListRef.current.style.setProperty(
        "--underline-x",
        `${rect.left - listRect.left}px`
      )
    } else {
      setActiveItem(null)
      menuListRef.current.style.setProperty("--underline-width", "0px")
      menuListRef.current.style.setProperty("--underline-x", "0px")
    }
  }, [pathname])

  const handleLinkClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setMobileMenuOpen(false)
      setOpenGroupsSheets({})

      // Mettre à jour la position de la bordure pour le lien cliqué
      const target = event.currentTarget
      if (!menuListRef.current) return

      const rect = target.getBoundingClientRect()
      const listRect = menuListRef.current.getBoundingClientRect()

      menuListRef.current.style.setProperty(
        "--underline-width",
        `${rect.width}px`
      )
      menuListRef.current.style.setProperty(
        "--underline-x",
        `${rect.left - listRect.left}px`
      )
      setActiveItem(target)
    },
    []
  )

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
      setSearchOpen(false)
      setSearchQuery("")
    }
  }

  // Focus search input when search is opened
  React.useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [searchOpen])

  // If no menu data available, show a warning in dev mode
  if (!menu) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "[DrupalMenu] No menu data available. Check Drupal configuration."
      )
    }
    return null
  }

  // Defensive check: ensure menu.items exists and is an array
  // This prevents "Cannot read properties of undefined (reading 'length')" errors
  if (!menu.items || !Array.isArray(menu.items)) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "[DrupalMenu] Menu items is missing or invalid. Falling back to empty menu."
      )
    }
    return null
  }

  // Note: Drupal GraphQL schema doesn't expose 'enabled' or 'weight' fields
  // All items returned by GraphQL are already enabled
  // Sort by array order (Drupal handles the ordering)
  // Use route.url field which contains the full path from Drupal
  const enabledItems = menu.items

  return (
    <div className={cn("", className)}>
      <div className="flex items-center">
        <div className="flex-1">
          <AppTitle className="mr-8" showLabel={false} size="md" />
        </div>
        <NavigationMenu
          className="hidden h-16 md:h-[90px] md:flex"
          viewport={false}
        >
          <NavigationMenuList ref={menuListRef} onMouseLeave={handleMouseLeave}>
            {enabledItems.map((item) => {
              // Check if item has children (ignore expanded flag)
              // Ensure children is always an array (defensive check)
              const hasSubmenu =
                Array.isArray(item.children) && item.children.length > 0
              const itemUrl = getMenuItemUrl(item)
              const isExternal = isExternalUrl(itemUrl)
              // Use smart active detection that checks children URLs too
              const isActive = isMenuItemActive(item, pathname)

              return hasSubmenu ? (
                <NavigationMenuItem
                  key={item.id}
                  className="relative h-14 md:h-[78px]"
                >
                  <NavigationMenuTrigger
                    onMouseEnter={handleMouseEnter}
                    className={cn(
                      "h-full whitespace-nowrap",
                      isActive && "text-accent-foreground"
                    )}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {item.title}
                  </NavigationMenuTrigger>

                  <NavigationMenuContent className="top-[calc(100%+0.25rem)]! left-0!">
                    <div className="w-[280px] rounded-none border-none p-6">
                      <div className="space-y-3">
                        {item.description && (
                          <h4 className="text-base font-semibold">
                            {item.title}
                          </h4>
                        )}
                        <ul className="grid gap-2">
                          {item.children?.map((child) => {
                            const childUrl = getMenuItemUrl(child)
                            const childIsExternal = isExternalUrl(childUrl)
                            return (
                              <li key={child.id}>
                                <NavigationMenuLink asChild>
                                  <Link
                                    href={childUrl}
                                    onClick={handleLinkClick}
                                    target={
                                      childIsExternal ? "_blank" : undefined
                                    }
                                    rel={
                                      childIsExternal
                                        ? "noopener noreferrer"
                                        : undefined
                                    }
                                    className="grid min-h-[44px] w-full items-center justify-start gap-1 rounded-md px-3 py-3 text-base font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                  >
                                    <div className="text-base leading-normal font-medium">
                                      {child.title}
                                    </div>
                                    {child.description && (
                                      <div className="text-sm text-muted-foreground">
                                        {child.description}
                                      </div>
                                    )}
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              ) : (
                <NavigationMenuItem key={item.id} className="h-16 md:h-[90px]">
                  {item.description ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <NavigationMenuLink active={isActive} asChild>
                          <Link
                            href={itemUrl}
                            onMouseEnter={handleMouseEnter}
                            onClick={handleLinkClick}
                            target={isExternal ? "_blank" : undefined}
                            rel={isExternal ? "noopener noreferrer" : undefined}
                            className={cn(
                              "h-full whitespace-nowrap",
                              isActive && "text-accent-foreground"
                            )}
                            aria-current={isActive ? "page" : undefined}
                          >
                            {item.title}
                          </Link>
                        </NavigationMenuLink>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>{item.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <NavigationMenuLink active={isActive} asChild>
                      <Link
                        href={itemUrl}
                        onMouseEnter={handleMouseEnter}
                        onClick={handleLinkClick}
                        target={isExternal ? "_blank" : undefined}
                        rel={isExternal ? "noopener noreferrer" : undefined}
                        className={cn(
                          "h-full whitespace-nowrap",
                          isActive && "text-accent-foreground"
                        )}
                        aria-current={isActive ? "page" : undefined}
                      >
                        {item.title}
                      </Link>
                    </NavigationMenuLink>
                  )}
                </NavigationMenuItem>
              )
            })}
          </NavigationMenuList>
        </NavigationMenu>

        {/* Search Button and Input */}
        <Sheet open={searchOpen} onOpenChange={setSearchOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="ml-2">
              <Search className="size-5" />
              <span className="sr-only">Search</span>
            </Button>
          </SheetTrigger>
          <SheetContent
            side="top"
            className="top-0 w-full p-2 md:p-4 [&>button]:hidden [&>div[data-slot=sheet-overlay]]:hidden"
            onInteractOutside={() => setSearchOpen(false)}
            onEscapeKeyDown={() => setSearchOpen(false)}
          >
            <SheetHeader>
              <div className="flex items-center justify-end mb-4">
                <SheetTitle className="sr-only">Search</SheetTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchOpen(false)}
                  aria-label="Close search"
                >
                  <X className="size-5" />
                </Button>
              </div>
            </SheetHeader>
            <form
              onSubmit={handleSearch}
              className="mx-auto flex w-full max-w-2xl gap-2"
            >
              <Input
                ref={searchInputRef}
                type="search"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" variant="ghost" size="icon">
                <Search className="size-5" />
                <span className="sr-only">Search</span>
              </Button>
            </form>
          </SheetContent>
        </Sheet>

        {/* Mobile Navigation */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              aria-label={
                mobileMenuOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X className="size-6" />
              ) : (
                <Menu className="size-6" />
              )}
              <span className="sr-only">
                {mobileMenuOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"}
              </span>
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="top-0 w-full overflow-y-auto p-2 md:p-4 [&>button]:hidden [&>div[data-slot=sheet-overlay]]:hidden"
            onInteractOutside={() => setMobileMenuOpen(false)}
            onEscapeKeyDown={() => setMobileMenuOpen(false)}
          >
            <div className="flex items-center justify-between mb-4 pb-4 border-b">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close navigation menu"
              >
                <X className="size-5" />
              </Button>
            </div>
            <nav className="grid gap-6 py-2" aria-label="Mobile navigation">
              {enabledItems.map((item) => {
                // Ensure children is always an array (defensive check)
                const hasSubmenu =
                  Array.isArray(item.children) && item.children.length > 0
                const itemUrl = getMenuItemUrl(item)
                const isExternal = isExternalUrl(itemUrl)
                // Use smart active detection that checks children URLs too
                const isActive = isMenuItemActive(item, pathname)

                // Render items without submenu (simple links)
                if (!hasSubmenu) {
                  const linkContent = (
                    <Link
                      href={itemUrl}
                      onClick={handleLinkClick}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noopener noreferrer" : undefined}
                      className="hover:text-accent-foreground flex w-full items-center py-1 text-lg font-semibold transition-colors"
                      aria-current={isActive ? "page" : undefined}
                    >
                      {item.title}
                    </Link>
                  )

                  return (
                    <div key={item.id} className="flex flex-col">
                      {item.description ? (
                        <Tooltip>
                          <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                          <TooltipContent side="right">
                            <p>{item.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        linkContent
                      )}
                    </div>
                  )
                }

                // Render items with submenu
                const sheetKey = `menu-${item.id}`
                const isSheetOpen = openGroupsSheets[sheetKey] || false

                return (
                  <div key={item.id} className="flex flex-col">
                    <Sheet
                      open={isSheetOpen}
                      onOpenChange={(open) => {
                        setOpenGroupsSheets((prev) => ({
                          ...prev,
                          [sheetKey]: open,
                        }))
                      }}
                    >
                      <SheetTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="hover:text-accent-foreground flex w-full items-center py-1 text-lg font-semibold transition-colors"
                          aria-label={`Toggle ${item.title} menu`}
                          aria-expanded={isSheetOpen}
                        >
                          {item.title}
                          <ChevronRight className="ml-auto size-5 transition-transform duration-200" />
                        </Button>
                      </SheetTrigger>
                      <SheetContent
                        side="left"
                        className="top-0 w-full gap-0 overflow-y-auto [&>button]:hidden [&>div[data-slot=sheet-overlay]]:hidden"
                        onInteractOutside={() => {
                          setOpenGroupsSheets((prev) => ({
                            ...prev,
                            [sheetKey]: false,
                          }))
                        }}
                        onEscapeKeyDown={() => {
                          setOpenGroupsSheets((prev) => ({
                            ...prev,
                            [sheetKey]: false,
                          }))
                        }}
                      >
                        <SheetHeader className="border-accent border-y p-1">
                          <div className="flex items-center justify-between">
                            <SheetTitle className="flex items-center gap-2">
                              <SheetClose asChild>
                                <Button
                                  variant="ghost"
                                  size="lg"
                                  className="text-lg"
                                >
                                  <ChevronLeft />
                                  <span className="sr-only">Back</span>
                                  {item.title}
                                </Button>
                              </SheetClose>
                            </SheetTitle>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setOpenGroupsSheets((prev) => ({
                                  ...prev,
                                  [sheetKey]: false,
                                }))
                              }}
                              aria-label="Close menu"
                            >
                              <X className="size-5" />
                            </Button>
                          </div>
                        </SheetHeader>
                        <div className="grid grid-cols-1 gap-6 p-6">
                          <div>
                            {item.description && (
                              <h4 className="text-muted-foreground mb-3 text-base font-semibold">
                                {item.description}
                              </h4>
                            )}
                            <div className="grid gap-2">
                              {item.children?.map((child) => {
                                const childUrl = getMenuItemUrl(child)
                                const childIsExternal = isExternalUrl(childUrl)
                                return (
                                  <Link
                                    key={child.id}
                                    href={childUrl}
                                    onClick={handleLinkClick}
                                    target={
                                      childIsExternal ? "_blank" : undefined
                                    }
                                    rel={
                                      childIsExternal
                                        ? "noopener noreferrer"
                                        : undefined
                                    }
                                    className="hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 flex min-h-[44px] w-full items-center rounded-md px-4 py-3 text-base font-medium transition-colors"
                                  >
                                    {child.title}
                                  </Link>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      </SheetContent>
                    </Sheet>
                    {item.description && (
                      <p className="text-muted-foreground text-sm">
                        {item.description}
                      </p>
                    )}
                  </div>
                )
              })}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  )
}
